import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import interior from "@/assets/interior.jpg";
import toast from "@/assets/toast.jpg";
import burrito from "@/assets/burrito.jpg";

const cards = [
  {
    src: interior,
    caption: "The Bar",
    tag: "BEHIND THE TAPS",
    aspect: "aspect-[4/3]",
    baseRot: -3.5,
    offset: "md:mt-10",
  },
  {
    src: toast,
    caption: "Avocado Toast",
    tag: "FRESH FROM THE KITCHEN",
    aspect: "aspect-[3/4]",
    baseRot: 2.5,
    offset: "md:-mt-4",
  },
  {
    src: burrito,
    caption: "The Burrito",
    tag: "SUNDAY MORNING",
    aspect: "aspect-[4/3]",
    baseRot: -2,
    offset: "md:mt-6",
  },
];

export default function Gallery() {
  return (
    <section
      data-testid="gallery-section"
      className="relative bg-[#0B132B] py-16 md:py-24 overflow-hidden"
    >
      {/* Ambient blue glow (energy) */}
      <div className="absolute inset-0 pointer-events-none opacity-80">
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[900px] h-[520px] rounded-full bg-primary/15 blur-[140px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
              </span>
              <span className="font-label text-xs text-primary tracking-[0.28em]">
                Live from the pub
              </span>
            </div>
            <h2 className="font-display text-5xl md:text-7xl text-[#F8F5F2] leading-[0.92]">
              Around the{" "}
              <span className="relative inline-block">
                <motion.span
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.35 }}
                  style={{ transformOrigin: "left" }}
                  className="absolute left-[-4%] right-[-4%] bottom-1 h-5 md:h-6 bg-primary/75 -rotate-1 rounded-sm"
                />
                <span className="relative">pub</span>
              </span>
              .
            </h2>
          </div>
          <div className="flex items-start gap-3 text-[#F8F5F2]/70 max-w-sm">
            <Sparkles className="h-5 w-5 text-primary shrink-0 mt-1" />
            <p className="text-sm leading-relaxed">
              A visual love letter to our regulars — snap-outs, plate-ups, and
              behind-the-bar moments straight from Bellevue.
            </p>
          </div>
        </div>

        {/* Polaroid photo wall */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-14">
          {cards.map((c, i) => (
            <motion.div
              key={c.caption}
              initial={{ opacity: 0, y: 90, rotate: c.baseRot * 4 }}
              whileInView={{ opacity: 1, y: 0, rotate: c.baseRot }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                type: "spring",
                stiffness: 90,
                damping: 14,
                delay: i * 0.12,
              }}
              whileHover={{
                rotate: 0,
                y: -14,
                scale: 1.035,
                transition: { type: "spring", stiffness: 220, damping: 15 },
              }}
              className={`relative ${c.offset} group cursor-pointer`}
            >
              <div className="relative bg-[#F8F5F2] p-3 pb-16 rounded-sm shadow-[0_25px_60px_-20px_rgba(0,0,0,0.65)] transition-shadow duration-500 group-hover:shadow-[0_40px_90px_-15px_rgba(30,140,215,0.45)]">
                {/* Tape */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 h-6 w-24 bg-primary/40 backdrop-blur-sm rotate-[-3deg] rounded-[2px] shadow-md border border-primary/30" />
                {/* Tag */}
                <div className="absolute top-6 left-6 z-10 bg-[#0B132B] text-[#F8F5F2] px-3 py-1 font-label text-[9px] tracking-[0.22em]">
                  {c.tag}
                </div>
                <div className={`relative overflow-hidden ${c.aspect}`}>
                  <motion.img
                    src={c.src}
                    alt={c.caption}
                    className="h-full w-full object-cover"
                    draggable={false}
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.06 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                  />
                </div>
                <div className="absolute bottom-5 left-6 right-6 flex items-baseline justify-between">
                  <span className="font-display text-2xl md:text-3xl text-[#0B132B] leading-none">
                    {c.caption}
                  </span>
                  <span className="font-label text-[10px] text-[#0B132B]/60">
                    #alleypub
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
